/**
 * BkStagesSettings — CRUD-Verwaltung der BK Stages (Betreuungskraft-Pipeline).
 *
 * Eigenständige Komponente nach dem Muster von PhasenSettings, aber gegen die
 * BK-Endpoints (v2BkPhases + v2BkPhaseRules aus src/lib/v2bk.ts) und mit
 * i18n-Keys settings.bk_stages.* (de/en/pl).
 *
 * Lädt BK Stages via GET /v2/admin/bk-phases, erlaubt Anlegen, Bearbeiten,
 * Löschen und Umsortieren (Up/Down) sowie das Regelsystem für automatische
 * Stage-Wechsel (GET/POST/PUT/DELETE /v2/admin/bk-phase-rules).
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field, Input, Badge } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { v2BkPhases, v2BkPhaseRules } from '../../lib/v2bk';
import { v2PhaseActions, type PhaseActionRow } from '../../lib/v1';
import type { PbPhase, PhaseCreatePayload, PhaseRule, PhaseRuleField } from '../../lib/v1';
import { StageActionsEditor } from './StageActionsEditor';

interface BkStagesSettingsProps {
  dark: boolean;
}

interface EditState {
  title: string;
  color: string;
  description: string;
  is_active: boolean;
}

export function BkStagesSettings({ dark }: BkStagesSettingsProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [stages, setStages] = useState<PbPhase[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Inline edit state: stageId → null = no edit open
  const [editId, setEditId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState>({ title: '', color: '#6b7280', description: '', is_active: true });
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Add-form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addState, setAddState] = useState<EditState>({ title: '', color: '#6b7280', description: '', is_active: true });
  const [addError, setAddError] = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState(false);

  // Per-row delete errors
  const [deleteErrors, setDeleteErrors] = useState<Record<number, string>>({});
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Stage-Regelsystem: Bedingungen je Ziel-Stage
  const [rules, setRules] = useState<PhaseRule[]>([]);
  const [ruleFields, setRuleFields] = useState<PhaseRuleField[]>([]);
  const [ruleError, setRuleError] = useState<string | null>(null);
  const [newRuleField, setNewRuleField] = useState('');
  const [newRuleOperator, setNewRuleOperator] = useState<'not_empty' | 'equals'>('not_empty');
  const [newRuleValue, setNewRuleValue] = useState('');
  const [ruleSaving, setRuleSaving] = useState(false);
  // ruleEditingId !== null → das Formular unten bearbeitet eine bestehende
  // Regel (vorbefüllt) statt eine neue anzulegen.
  const [ruleEditingId, setRuleEditingId] = useState<number | null>(null);

  // Phasen-Aktionen (Auslöser → E-Mail/Aufgabe) je BK Stage
  const [actions, setActions] = useState<PhaseActionRow[]>([]);

  const loadActions = async () => {
    try {
      setActions(await v2PhaseActions.list('bk'));
    } catch { /* Aktionen optional — Fehler still */ }
  };

  const loadRules = async () => {
    try {
      const [r, f] = await Promise.all([v2BkPhaseRules.list(), v2BkPhaseRules.fields()]);
      setRules(r);
      setRuleFields(f);
    } catch { /* Regeln optional — Fehler still */ }
  };

  /** Formular auf den Ausgangszustand zurücksetzen (auch nach „Abbrechen"). */
  const resetRuleForm = () => {
    setRuleEditingId(null);
    setNewRuleField('');
    setNewRuleOperator('not_empty');
    setNewRuleValue('');
  };

  /** Bestehende Regel ins Formular laden. */
  const startEditRule = (rule: PhaseRule) => {
    setRuleEditingId(rule.id);
    setRuleError(null);
    setNewRuleField(rule.field);
    setNewRuleOperator(rule.operator);
    setNewRuleValue(rule.operator === 'equals' ? (rule.value ?? '') : '');
  };

  const submitRule = async (phaseId: number) => {
    if (!newRuleField) return;
    setRuleSaving(true);
    setRuleError(null);
    try {
      const payload = {
        field: newRuleField,
        operator: newRuleOperator,
        value: newRuleOperator === 'equals' ? newRuleValue : null,
      };
      if (ruleEditingId !== null) {
        const updated = await v2BkPhaseRules.update(ruleEditingId, payload);
        setRules(prev => prev.map(r => (r.id === updated.id ? updated : r)));
      } else {
        const created = await v2BkPhaseRules.create({ phase_id: phaseId, ...payload });
        setRules(prev => [...prev, created]);
      }
      resetRuleForm();
    } catch {
      setRuleError(ruleEditingId !== null
        ? tr('settings.bk_stages.rules.error_save')
        : tr('settings.bk_stages.rules.error_create'));
    } finally {
      setRuleSaving(false);
    }
  };

  const removeRule = async (id: number) => {
    try {
      await v2BkPhaseRules.remove(id);
      setRules(prev => prev.filter(r => r.id !== id));
      // Sonst zeigt das Formular weiter auf eine Regel, die es nicht mehr gibt.
      if (ruleEditingId === id) resetRuleForm();
    } catch {
      setRuleError(tr('settings.bk_stages.rules.error_delete'));
    }
  };

  const toggleRule = async (rule: PhaseRule) => {
    try {
      const updated = await v2BkPhaseRules.update(rule.id, { aktiv: !rule.aktiv });
      setRules(prev => prev.map(r => r.id === updated.id ? updated : r));
    } catch {
      setRuleError(tr('settings.bk_stages.rules.error_update'));
    }
  };

  const loadStages = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await v2BkPhases.list();
      setStages(data.slice().sort((a, b) => a.sort - b.sort));
    } catch {
      setLoadError(tr('settings.bk_stages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRules();
    void loadActions();
    void loadStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUp = async (idx: number) => {
    if (idx === 0) return;
    const next = [...stages];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setStages(next);
    try {
      await v2BkPhases.reorder(next.map(p => p.id));
    } catch {
      // revert
      void loadStages();
    }
  };

  const handleDown = async (idx: number) => {
    if (idx === stages.length - 1) return;
    const next = [...stages];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setStages(next);
    try {
      await v2BkPhases.reorder(next.map(p => p.id));
    } catch {
      void loadStages();
    }
  };

  const openEdit = (stage: PbPhase) => {
    setEditId(stage.id);
    setEditState({
      title: stage.title,
      color: stage.color || '#6b7280',
      description: stage.description ?? '',
      is_active: stage.is_active,
    });
    setEditError(null);
    // Sonst könnte ein offen gebliebenes Regel-Formular einer anderen Stage
    // hier weiterlaufen und beim Speichern die falsche Regel überschreiben.
    resetRuleForm();
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditError(null);
    resetRuleForm();
  };

  const saveEdit = async () => {
    if (!editId) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await v2BkPhases.update(editId, {
        title: editState.title,
        color: editState.color,
        description: editState.description || null,
        is_active: editState.is_active,
      });
      setStages(prev => prev.map(p => p.id === editId ? updated : p));
      setEditId(null);
    } catch {
      setEditError(tr('settings.bk_stages.error.save'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    setDeleteErrors(prev => ({ ...prev, [id]: '' }));
    try {
      await v2BkPhases.remove(id);
      setStages(prev => prev.filter(p => p.id !== id));
    } catch (e: unknown) {
      const axiosErr = e as { response?: { data?: { errors?: Array<{ code?: string }> }; status?: number } };
      const code = axiosErr?.response?.data?.errors?.[0]?.code;
      setDeleteErrors(prev => ({
        ...prev,
        [id]: code === 'E_PHASE_IN_USE'
          ? tr('settings.bk_stages.error.in_use')
          : tr('settings.bk_stages.error.delete'),
      }));
    } finally {
      setDeletingId(null);
    }
  };

  const handleAdd = async () => {
    if (!addState.title.trim()) {
      setAddError(tr('settings.bk_stages.error.title_required'));
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      const payload: PhaseCreatePayload = {
        title: addState.title.trim(),
        color: addState.color,
        description: addState.description || null,
        is_active: addState.is_active,
      };
      const created = await v2BkPhases.create(payload);
      setStages(prev => [...prev, created]);
      setAddState({ title: '', color: '#6b7280', description: '', is_active: true });
      setShowAddForm(false);
    } catch {
      setAddError(tr('settings.bk_stages.error.create'));
    } finally {
      setAddSaving(false);
    }
  };

  const ROW_STYLE: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 0',
    borderBottom: `1px solid ${t.borderSoft}`,
    flexWrap: 'wrap',
  };

  const INPUT_STYLE: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: 8,
    padding: '7px 10px', fontSize: 13, color: t.heading,
    background: t.inputBg, fontFamily: 'Outfit, sans-serif',
    outline: 'none', width: '100%',
  };

  if (loading) {
    return (
      <Card dark={dark} style={{ padding: 20 }}>
        <div style={{ color: t.muted, fontSize: 13 }}>{tr('settings.bk_stages.loading')}</div>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card dark={dark} style={{ padding: 20 }}>
        <div style={{ color: '#b42318', fontSize: 13, marginBottom: 12 }}>{loadError}</div>
        <Button variant="secondary" size="sm" dark={dark} onClick={() => void loadStages()}>
          {tr('settings.bk_stages.retry')}
        </Button>
      </Card>
    );
  }

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <SectionTitle
        dark={dark}
        title={tr('settings.bk_stages.title')}
        subtitle={tr('settings.bk_stages.subtitle')}
      />

      {stages.map((stage, idx) => (
        <div key={stage.id}>
          {/* Row */}
          <div style={ROW_STYLE}>
            {/* Color swatch */}
            <div style={{
              width: 16, height: 16, borderRadius: 4, flexShrink: 0,
              background: stage.color || '#6b7280',
              border: `1px solid ${t.border}`,
            }} />

            {/* Sort badge */}
            <Badge tone="neutral" dark={dark}>{stage.sort}</Badge>

            {/* Title + description */}
            <div style={{ flex: 1, minWidth: 100 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{stage.title}</div>
              {stage.description && (
                <div style={{
                  fontSize: 11, color: t.muted, marginTop: 2,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260,
                }}>
                  {stage.description}
                </div>
              )}
            </div>

            {/* Bedingungen-Zähler */}
            {rules.filter(r => r.phase_id === stage.id).length > 0 && (
              <Badge tone="info" dark={dark}>
                {tr('settings.bk_stages.rules.badge', { count: rules.filter(r => r.phase_id === stage.id).length })}
              </Badge>
            )}

            {/* Aktionen-Zähler */}
            {actions.filter(a => a.phase_id === stage.id).length > 0 && (
              <Badge tone="info" dark={dark}>
                {tr('settings.bk_stages.actions_badge', { count: actions.filter(a => a.phase_id === stage.id).length })}
              </Badge>
            )}

            {/* Active badge */}
            <Badge tone={stage.is_active ? 'success' : 'neutral'} dark={dark}>
              {stage.is_active ? tr('settings.bk_stages.active') : tr('settings.bk_stages.inactive')}
            </Badge>

            {/* Up/Down */}
            <button
              aria-label={tr('settings.bk_stages.up')}
              disabled={idx === 0}
              onClick={() => void handleUp(idx)}
              style={{
                background: 'none', border: `1px solid ${t.border}`, borderRadius: 6,
                padding: '3px 8px', cursor: idx === 0 ? 'not-allowed' : 'pointer',
                color: idx === 0 ? t.muted : t.text, fontSize: 12,
                fontFamily: 'Outfit, sans-serif',
              }}
            >↑</button>
            <button
              aria-label={tr('settings.bk_stages.down')}
              disabled={idx === stages.length - 1}
              onClick={() => void handleDown(idx)}
              style={{
                background: 'none', border: `1px solid ${t.border}`, borderRadius: 6,
                padding: '3px 8px', cursor: idx === stages.length - 1 ? 'not-allowed' : 'pointer',
                color: idx === stages.length - 1 ? t.muted : t.text, fontSize: 12,
                fontFamily: 'Outfit, sans-serif',
              }}
            >↓</button>

            {/* Edit */}
            <Button
              variant="secondary"
              size="sm"
              dark={dark}
              onClick={() => openEdit(stage)}
            >
              {tr('settings.bk_stages.edit')}
            </Button>

            {/* Delete */}
            <Button
              variant="secondary"
              size="sm"
              dark={dark}
              disabled={deletingId === stage.id}
              onClick={() => void handleDelete(stage.id)}
            >
              {deletingId === stage.id ? '…' : tr('settings.bk_stages.delete')}
            </Button>
          </div>

          {/* Delete error */}
          {deleteErrors[stage.id] && (
            <div style={{ fontSize: 12, color: '#b42318', marginBottom: 6 }}>
              {deleteErrors[stage.id]}
            </div>
          )}

          {/* Inline edit form */}
          {editId === stage.id && (
            <div style={{
              padding: 14, background: t.cardMuted, border: `1px solid ${t.borderSoft}`,
              borderRadius: 10, marginBottom: 8,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label={tr('settings.bk_stages.field.title')}>
                  <Input
                    value={editState.title}
                    onChange={e => setEditState(s => ({ ...s, title: e.target.value }))}
                    dark={dark}
                  />
                </Field>
                <Field label={tr('settings.bk_stages.field.color')}>
                  <input
                    type="color"
                    value={editState.color}
                    onChange={e => setEditState(s => ({ ...s, color: e.target.value }))}
                    style={{ width: '100%', height: 38, border: `1px solid ${t.border}`, borderRadius: 8, cursor: 'pointer', background: 'none' }}
                  />
                </Field>
                <Field label={tr('settings.bk_stages.field.description')}>
                  <input
                    value={editState.description}
                    onChange={e => setEditState(s => ({ ...s, description: e.target.value }))}
                    style={INPUT_STYLE}
                    placeholder={tr('settings.bk_stages.field.description_placeholder')}
                  />
                </Field>
                <Field label={tr('settings.bk_stages.field.status')}>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    {[true, false].map(v => (
                      <button
                        key={String(v)}
                        onClick={() => setEditState(s => ({ ...s, is_active: v }))}
                        style={{
                          flex: 1, padding: '8px 0', borderRadius: 8, border: `1px solid ${editState.is_active === v ? '#22c55e' : t.border}`,
                          background: editState.is_active === v ? '#ecfdf3' : t.card,
                          color: editState.is_active === v ? '#027a48' : t.text,
                          cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'Outfit, sans-serif',
                        }}
                      >
                        {v ? tr('settings.bk_stages.active') : tr('settings.bk_stages.inactive')}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>

              {/* ── Bedingungen für den Wechsel in diese Stage ── */}
              <div style={{ borderTop: `1px solid ${t.borderSoft}`, paddingTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  {tr('settings.bk_stages.rules.heading')}
                </div>
                <div style={{ fontSize: 11, color: t.muted, marginBottom: 10 }}>
                  {tr('settings.bk_stages.rules.hint')}
                </div>

                {rules.filter(r => r.phase_id === stage.id).map(rule => (
                  <div key={rule.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', background: t.card, border: `1px solid ${t.border}`,
                    borderRadius: 8, marginBottom: 6, fontSize: 12,
                  }}>
                    <span style={{ flex: 1, color: rule.aktiv ? t.text : t.muted, textDecoration: rule.aktiv ? 'none' : 'line-through' }}>
                      {rule.field_label}
                      {rule.operator === 'equals'
                        ? ` ${tr('settings.bk_stages.rules.op_equals')} "${rule.value}"`
                        : ` ${tr('settings.bk_stages.rules.op_not_empty')}`}
                    </span>
                    <button
                      onClick={() => startEditRule(rule)}
                      style={{ background: 'none', border: `1px solid ${ruleEditingId === rule.id ? t.heading : t.border}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: t.muted, fontFamily: 'Outfit, sans-serif' }}
                    >
                      {tr('settings.bk_stages.rules.edit')}
                    </button>
                    <button
                      onClick={() => void toggleRule(rule)}
                      style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: t.muted, fontFamily: 'Outfit, sans-serif' }}
                    >
                      {rule.aktiv ? tr('settings.bk_stages.rules.deactivate') : tr('settings.bk_stages.rules.activate')}
                    </button>
                    <button
                      onClick={() => void removeRule(rule.id)}
                      style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: '#b42318', fontFamily: 'Outfit, sans-serif' }}
                    >
                      {tr('settings.bk_stages.rules.delete')}
                    </button>
                  </div>
                ))}

                {/* Neue Regel */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                  <select
                    value={newRuleField}
                    onChange={e => setNewRuleField(e.target.value)}
                    style={{ ...INPUT_STYLE, width: 'auto', flex: 1, minWidth: 160 }}
                  >
                    <option value="">{tr('settings.bk_stages.rules.select_field')}</option>
                    {ruleFields.map(f => (
                      <option key={f.field} value={f.field}>{f.label}</option>
                    ))}
                  </select>
                  <select
                    value={newRuleOperator}
                    onChange={e => setNewRuleOperator(e.target.value as 'not_empty' | 'equals')}
                    style={{ ...INPUT_STYLE, width: 'auto' }}
                  >
                    <option value="not_empty">{tr('settings.bk_stages.rules.op_not_empty')}</option>
                    <option value="equals">{tr('settings.bk_stages.rules.op_equals')}</option>
                  </select>
                  {newRuleOperator === 'equals' && (
                    <input
                      value={newRuleValue}
                      onChange={e => setNewRuleValue(e.target.value)}
                      placeholder={tr('settings.bk_stages.rules.value_placeholder')}
                      style={{ ...INPUT_STYLE, width: 100 }}
                    />
                  )}
                  <Button
                    variant="secondary" size="sm" dark={dark}
                    disabled={ruleSaving || !newRuleField || (newRuleOperator === 'equals' && !newRuleValue)}
                    onClick={() => void submitRule(stage.id)}
                  >
                    {ruleSaving ? '…' : (ruleEditingId !== null ? tr('settings.bk_stages.rules.save') : tr('settings.bk_stages.rules.add'))}
                  </Button>
                  {ruleEditingId !== null && (
                    <Button variant="ghost" size="sm" dark={dark} onClick={resetRuleForm}>
                      {tr('settings.bk_stages.rules.cancel')}
                    </Button>
                  )}
                </div>

                {ruleError && <div style={{ fontSize: 12, color: '#b42318', marginTop: 6 }}>{ruleError}</div>}
              </div>

              {/* ── Aktionen (Auslöser → E-Mail/Aufgabe) ── */}
              <StageActionsEditor
                dark={dark}
                scope="bk"
                phaseId={stage.id}
                actions={actions}
                onChange={setActions}
              />

              {editError && <div style={{ fontSize: 12, color: '#b42318' }}>{editError}</div>}

              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="primary" size="sm" dark={dark} disabled={editSaving} onClick={() => void saveEdit()}>
                  {editSaving ? tr('settings.bk_stages.saving') : tr('settings.bk_stages.save')}
                </Button>
                <Button variant="secondary" size="sm" dark={dark} onClick={cancelEdit}>
                  {tr('settings.bk_stages.cancel')}
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add form */}
      {showAddForm ? (
        <div style={{
          padding: 14, background: t.cardMuted, border: `1px solid ${t.borderSoft}`,
          borderRadius: 10, marginTop: 14,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 4 }}>
            {tr('settings.bk_stages.new_heading')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label={tr('settings.bk_stages.field.title')}>
              <Input
                value={addState.title}
                onChange={e => setAddState(s => ({ ...s, title: e.target.value }))}
                dark={dark}
                placeholder={tr('settings.bk_stages.field.title_placeholder')}
              />
            </Field>
            <Field label={tr('settings.bk_stages.field.color')}>
              <input
                type="color"
                value={addState.color}
                onChange={e => setAddState(s => ({ ...s, color: e.target.value }))}
                style={{ width: '100%', height: 38, border: `1px solid ${t.border}`, borderRadius: 8, cursor: 'pointer', background: 'none' }}
              />
            </Field>
            <Field label={tr('settings.bk_stages.field.description')}>
              <input
                value={addState.description}
                onChange={e => setAddState(s => ({ ...s, description: e.target.value }))}
                style={INPUT_STYLE}
                placeholder={tr('settings.bk_stages.field.description_placeholder')}
              />
            </Field>
            <Field label={tr('settings.bk_stages.field.status')}>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {[true, false].map(v => (
                  <button
                    key={String(v)}
                    onClick={() => setAddState(s => ({ ...s, is_active: v }))}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, border: `1px solid ${addState.is_active === v ? '#22c55e' : t.border}`,
                      background: addState.is_active === v ? '#ecfdf3' : t.card,
                      color: addState.is_active === v ? '#027a48' : t.text,
                      cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'Outfit, sans-serif',
                    }}
                  >
                    {v ? tr('settings.bk_stages.active') : tr('settings.bk_stages.inactive')}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          {addError && <div style={{ fontSize: 12, color: '#b42318' }}>{addError}</div>}

          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="primary" size="sm" dark={dark} disabled={addSaving} onClick={() => void handleAdd()}>
              {addSaving ? tr('settings.bk_stages.creating') : tr('settings.bk_stages.create')}
            </Button>
            <Button variant="secondary" size="sm" dark={dark} onClick={() => { setShowAddForm(false); setAddError(null); }}>
              {tr('settings.bk_stages.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          <Button variant="primary" size="sm" dark={dark} onClick={() => setShowAddForm(true)}>
            {tr('settings.bk_stages.add')}
          </Button>
        </div>
      )}
    </Card>
  );
}
